import React, { useState } from "react";
import "./JoinAsReviewer.css";
import { ApiUrl } from "../../../../Admin/API/ApiUrl";
import Swal from "sweetalert2";

export default function JoinAsReviewer() {
  const [formData, setFormData] = useState({
    fullName: "",
    qualification: "",
    institution: "",
    address: "",
    residentialContact: "",
    contactNumber: "",
    researchDomain: "",
    resume: null,
  });

  const [fileName, setFileName] = useState("No file chosen");
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFormData({ ...formData, resume: file });
    } else {
      setFileName("No file chosen");
      setFormData({ ...formData, resume: null });
    }
  };

  const nameRegex = /^[A-Za-z\s.]+$/;
  const textRegex = /^[A-Za-z0-9\s,.-]+$/;
  const phoneRegex = /^\+?[0-9\s\-]{10,15}$/;


  const validate = () => {
    let formErrors = {};

    // Full Name
    if (!formData.fullName.trim()) {
      formErrors.fullName = "Please enter your full name";
    } else if (!nameRegex.test(formData.fullName)) {
      formErrors.fullName = "Only letters allowed in name";
    }

    // Qualification
    if (!formData.qualification.trim()) {
      formErrors.qualification = "Please enter your qualification";
    } else if (!textRegex.test(formData.qualification)) {
      formErrors.qualification = "Invalid characters in qualification";
    }

    // Institution
    if (!formData.institution.trim()) {
      formErrors.institution = "Please enter institution/company name";
    } else if (!textRegex.test(formData.institution)) {
      formErrors.institution = "Invalid characters in institution name";
    }

    // Address
    if (!formData.address.trim()) {
      formErrors.address = "Please enter address";
    } else if (formData.address.length < 10) {
      formErrors.address = "Address must be at least 10 characters";
    } else if (!textRegex.test(formData.address)) {
      formErrors.address = "Invalid characters in address";
    }

    // Residential Contact
    if (!formData.residentialContact.trim()) {
      formErrors.residentialContact = "Enter residential contact details";
    } else if (!textRegex.test(formData.residentialContact)) {
      formErrors.residentialContact = "Invalid characters in contact details";
    }

    // Phone Number
    if (!formData.contactNumber.trim()) {
      formErrors.contactNumber = "Enter contact number";
    } else if (!phoneRegex.test(formData.contactNumber)) {
      formErrors.contactNumber =
        "Enter valid number (10–15 digits, with optional +)";
    }

    // Research Domain
    if (!formData.researchDomain.trim()) {
      formErrors.researchDomain = "Enter research domain";
    } else if (!textRegex.test(formData.researchDomain)) {
      formErrors.researchDomain = "Invalid characters in research domain";
    }

    // Resume Validation
    if (!formData.resume) {
      formErrors.resume = "Upload resume (.doc/.docx only)";
    } else {
      const file = formData.resume;
      const ext = file.name.split(".").pop().toLowerCase();

      if (!["doc", "docx"].includes(ext)) {
        formErrors.resume = "Only .doc or .docx files allowed";
      }

      if (file.size > 2 * 1024 * 1024) {
        formErrors.resume = "File size must be less than 2MB";
      }
    }

    // console.log("🛑 Validation Errors:", formErrors);

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      // Swal.fire({
      //   icon: "error",
      //   title: "Validation Error",
      //   text: "Please fill all required fields correctly.",
      // });
      return;
    }

    try {
      const formDataToSend = new FormData();

      formDataToSend.append("full_name", formData.fullName);
      formDataToSend.append("residential_contact", formData.residentialContact);
      formDataToSend.append("qualification", formData.qualification);
      formDataToSend.append("contact_number", formData.contactNumber);
      formDataToSend.append("institution_name", formData.institution);
      formDataToSend.append("research_domain", formData.researchDomain);
      formDataToSend.append("institution_address", formData.address);

      if (formData.resume) {
        formDataToSend.append("resume", formData.resume);
      }

      const response = await fetch(`${ApiUrl}/review-submission`, {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message);

      Swal.fire({
        icon: "success",
        title: "Submitted Successfully!",
        text: "Application submitted successfully.",
        confirmButtonColor: "#3085d6",
      });
      setFormData({
        fullName: "",
        qualification: "",
        institution: "",
        address: "",
        residentialContact: "",
        contactNumber: "",
        researchDomain: "",
        resume: null,
      })
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.message || "Something went wrong!",
      });
    }
  };

  return (
    <div className="reviewer-registration-container">
      <div className="reviewer-registration-header">
        <h1>IJACNA Editorial Board Reviewer Confirmation</h1>
        <p>Join our team of expert reviewers</p>
      </div>

      <form className="reviewer-registration-form" onSubmit={handleSubmit}>
        <div className="reviewer-two-column-layout">
          {/* LEFT SIDE */}
          <div className="reviewer-left-column">
            {/* Personal Information */}
            <div className="reviewer-form-section">
              <h2 className="reviewer-form-section-title">
                Personal Information
              </h2>

              <div className="reviewer-form-group">
                <label htmlFor="fullName" className="reviewer-form-label">
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  className={`reviewer-form-input ${errors.fullName ? "error" : ""
                    }`}
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                />
                {errors.fullName && (
                  <div className="reviewer-form-error">{errors.fullName}</div>
                )}
              </div>

              <div className="reviewer-form-group">
                <label htmlFor="qualification" className="reviewer-form-label">
                  Qualification
                </label>
                <input
                  type="text"
                  id="qualification"
                  className={`reviewer-form-input ${errors.qualification ? "error" : ""
                    }`}
                  placeholder="Enter your qualifications"
                  value={formData.qualification}
                  onChange={handleChange}
                />
                {errors.qualification && (
                  <div className="reviewer-form-error">
                    {errors.qualification}
                  </div>
                )}
              </div>
            </div>

            {/* Professional Information */}
            <div className="reviewer-form-section">
              <h2 className="reviewer-form-section-title">
                Professional Information
              </h2>

              <div className="reviewer-form-group">
                <label htmlFor="institution" className="reviewer-form-label">
                  Institution/Company Name
                </label>
                <input
                  type="text"
                  id="institution"
                  className={`reviewer-form-input ${errors.institution ? "error" : ""
                    }`}
                  placeholder="Enter institution/company name"
                  value={formData.institution}
                  onChange={handleChange}
                />
                {errors.institution && (
                  <div className="reviewer-form-error">
                    {errors.institution}
                  </div>
                )}
              </div>

              <div className="reviewer-form-group">
                <label htmlFor="address" className="reviewer-form-label">
                  Address of Institution/Company
                </label>
                <input
                  type="text"
                  id="address"
                  className={`reviewer-form-input ${errors.address ? "error" : ""
                    }`}
                  placeholder="Enter institution/company address"
                  value={formData.address}
                  onChange={handleChange}
                />
                {errors.address && (
                  <div className="reviewer-form-error">{errors.address}</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="reviewer-right-column">
            {/* Contact Information */}
            <div className="reviewer-form-section">
              <h2 className="reviewer-form-section-title">
                Contact Information
              </h2>

              <div className="reviewer-form-group">
                <label
                  htmlFor="residentialContact"
                  className="reviewer-form-label"
                >
                  Residential Contact Details
                </label>
                <input
                  type="text"
                  id="residentialContact"
                  className={`reviewer-form-input ${errors.residentialContact ? "error" : ""
                    }`}
                  placeholder="Enter residential contact details"
                  value={formData.residentialContact}
                  onChange={handleChange}
                />
                {errors.residentialContact && (
                  <div className="reviewer-form-error">
                    {errors.residentialContact}
                  </div>
                )}
              </div>

              <div className="reviewer-form-group">
                <label htmlFor="contactNumber" className="reviewer-form-label">
                  Contact Number with Country Code
                </label>
                <input
                  type="text"
                  id="contactNumber"
                  className={`reviewer-form-input ${errors.contactNumber ? "error" : ""
                    }`}
                  placeholder="e.g., +91 9876543210"
                  value={formData.contactNumber}
                  onChange={handleChange}
                />
                {errors.contactNumber && (
                  <div className="reviewer-form-error">
                    {errors.contactNumber}
                  </div>
                )}
              </div>
            </div>

            {/* Research Information */}
            <div className="reviewer-form-section">
              <h2 className="reviewer-form-section-title">
                Research Information
              </h2>

              <div className="reviewer-form-group">
                <label htmlFor="researchDomain" className="reviewer-form-label">
                  Research Domain Name/Area of Interest
                </label>
                <input
                  type="text"
                  id="researchDomain"
                  className={`reviewer-form-input ${errors.researchDomain ? "error" : ""
                    }`}
                  placeholder="Enter your research domain/area of interest"
                  value={formData.researchDomain}
                  onChange={handleChange}
                />
                {errors.researchDomain && (
                  <div className="reviewer-form-error">
                    {errors.researchDomain}
                  </div>
                )}
              </div>

              <div className="reviewer-form-group">
                <label className="reviewer-form-label">
                  Upload your Resume (.doc or .docx)
                </label>
                <div className="reviewer-file-upload">
                  <input
                    type="file"
                    id="resume"
                    className="reviewer-file-input"
                    accept=".doc,.docx"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="resume" className="reviewer-file-label">
                    <span>Choose File</span>
                    <span className="reviewer-file-name">{fileName}</span>
                  </label>
                </div>
                {errors.resume && (
                  <div className="reviewer-form-error">{errors.resume}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <button type="submit" className="reviewer-submit-btn">
          Submit
        </button>
        <div className="reviewer-required-note">* Required fields</div>
      </form>
    </div>
  );
}
